import { BigNumber } from "@nts/std/types";
import { NumeralService } from "./numeral.service";
import { ElementRef } from "@angular/core";
import IMask, { AppendFlags, ChangeDetails, ExtractFlags, Masked, MaskedNumber, MaskedNumberOptions, MaskedOptions, MaskedPattern, TailDetails } from 'imask';
import { Direction, DIRECTION, escapeRegExp } from "imask/esm/core/utils";

export interface PatternSettings {
  mask: string;
  expose: true;
}

export interface PatternBlocks {
  [key:string]: Partial<PatternSettings>
}

export interface PatternMaskSettings {
  mask: string;
  blocks: PatternBlocks;
}

export interface NumericMaskSettings {
  useThousandSeparator?: boolean,
  decimalLimit?: number;
  integerLimit?: number;
  min?: BigNumber | number;
  max?: BigNumber | number;
  nullable: boolean;

  /**
   * Flag to remove leading and trailing zeros in the end of editing
   */
  normalizeZeros?: boolean;

  /**
   * If true, then pads zeros at end to the length of scale
   */
  padFractionalZeros?: boolean;
  isBigNumber?: boolean;
  faultBackValueFunc?: () => string;

  patternMask?: PatternMaskSettings;
  lazy?: boolean;
  eager?: boolean | 'remove' | 'append' | undefined;
}

export
  type MaskedBigNumberOptions = MaskedOptions<NtsMaskedBigNumber,
    | 'radix'
    | 'thousandsSeparator'
    | 'mapToRadix'
    | 'scale'
    | 'min'
    | 'max'
    | 'normalizeZeros'
    | 'padFractionalZeros'
    | 'nullable'
  >;

export class NtsMaskedBigNumber extends Masked<BigNumber> {
  static UNMASKED_RADIX = '.';
  static override EMPTY_VALUES: Array<null | undefined | string | number> = [...Masked.EMPTY_VALUES, 0];
  static override DEFAULTS: Partial<MaskedBigNumberOptions> = {
    ...Masked.DEFAULTS,
    mask: Number,
    radix: ',',
    thousandsSeparator: '',
    mapToRadix: [
      MaskedNumber.UNMASKED_RADIX
    ],
    min: new BigNumber(Number.MIN_SAFE_INTEGER.toString()),
    max: new BigNumber(Number.MAX_SAFE_INTEGER.toString()),
    scale: 2,
    normalizeZeros: true,
    padFractionalZeros: true,
  };

  declare mask: NumberConstructor;
  /** Single char */
  declare radix: string;
  /** Single char */
  declare thousandsSeparator: string;
  /** Array of single chars */
  declare mapToRadix: Array<string>;
  /** */
  declare min: BigNumber;
  /** */
  declare max: BigNumber;
  /** Digits after point */
  declare scale: number;
  /** Flag to remove leading and trailing zeros in the end of editing */
  declare normalizeZeros: boolean;
  /** Flag to pad trailing zeros after point in the end of editing */
  declare padFractionalZeros: boolean;
  /** Enable characters overwriting */
  declare overwrite?: boolean | 'shift' | undefined;
  /** */
  declare eager?: boolean | 'remove' | 'append' | undefined;
  /** */
  declare skipInvalid?: boolean | undefined;
  /** */
  declare autofix?: boolean | 'pad' | undefined;
  /** Format typed value to string */
  declare format: (value: BigNumber, masked: Masked) => string;
  /** Parse string to get typed value */
  declare parse: (str: string, masked: Masked) => BigNumber;

  declare _numberRegExp: RegExp;
  declare _thousandsSeparatorRegExp: RegExp;
  declare _mapToRadixRegExp: RegExp;
  declare _separatorsProcessed: boolean;

  nullable = false;

  constructor(opts?: MaskedBigNumberOptions) {
    super({
      ...NtsMaskedBigNumber.DEFAULTS,
      ...opts,
    });
    this.nullable = opts.nullable;
  }

  override updateOptions(opts: Partial<MaskedNumberOptions>) {
    super.updateOptions(opts);
  }

  override _update(opts: Partial<MaskedNumberOptions>) {
    super._update(opts);
    this._updateRegExps();
  }

  _updateRegExps() {
    const start = '^' + (this.allowNegative ? '[+|\\-]?' : '');
    const mid = '\\d*';
    const end = (this.scale ?
      `(${escapeRegExp(this.radix)}\\d{0,${this.scale}})?` :
      '') + '$';

    this._numberRegExp = new RegExp(start + mid + end);
    this._mapToRadixRegExp = new RegExp(`[${this.mapToRadix.map(escapeRegExp).join('')}]`, 'g');
    this._thousandsSeparatorRegExp = new RegExp(escapeRegExp(this.thousandsSeparator), 'g');
  }

  _removeThousandsSeparators(value: string): string {
    return value.replace(this._thousandsSeparatorRegExp, '');
  }

  _insertThousandsSeparators(value: string): string {
    // https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
    const parts = value.split(this.radix);
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator);
    return parts.join(this.radix);
  }

  override doPrepareChar(ch: string, flags: AppendFlags = {}): [string, ChangeDetails] {
    const [prepCh, details] = super.doPrepareChar(this._removeThousandsSeparators(
      this.scale && this.mapToRadix.length && (
        /*
          radix should be mapped when
          1) input is done from keyboard = flags.input && flags.raw
          2) unmasked value is set = !flags.input && !flags.raw
          and should not be mapped when
          1) value is set = flags.input && !flags.raw
          2) raw value is set = !flags.input && flags.raw
        */
        flags.input && flags.raw ||
        !flags.input && !flags.raw
      ) ? ch.replace(this._mapToRadixRegExp, this.radix) : ch
    ), flags);
    if (ch && !prepCh) details.skip = true;

    if (prepCh && !this.allowPositive && !this.value && prepCh !== '-') details.aggregate(this._appendChar('-'));

    return [prepCh, details];
  }

  _separatorsCount(to: number, extendOnSeparators: boolean = false): number {
    let count = 0;

    for (let pos = 0; pos < to; ++pos) {
      if (this._value.indexOf(this.thousandsSeparator, pos) === pos) {
        ++count;
        if (extendOnSeparators) to += this.thousandsSeparator.length;
      }
    }

    return count;
  }

  _separatorsCountFromSlice(slice: string = this._value): number {
    return this._separatorsCount(this._removeThousandsSeparators(slice).length, true);
  }

  override extractInput(fromPos: number = 0, toPos: number = this.displayValue.length, flags?: ExtractFlags): string {
    [fromPos, toPos] = this._adjustRangeWithSeparators(fromPos, toPos);

    return this._removeThousandsSeparators(super.extractInput(fromPos, toPos, flags));
  }


  override _appendCharRaw(ch: string, flags: AppendFlags = {}): ChangeDetails {

    const prevBeforeTailValue = flags.tail && flags._beforeTailState ?
      flags._beforeTailState._value :
      this._value;
    const prevBeforeTailSeparatorsCount = this._separatorsCountFromSlice(prevBeforeTailValue);
    this._value = this._removeThousandsSeparators(this.value);

    const oldValue = this._value;

    this._value += ch;

    const num = this.number;
    let accepted = !(new BigNumber(num).isNaN());
    let skip = false;

    if (accepted) {
      let fixedNum;
      if (this.min != null && this.min.comparedTo(new BigNumber(0)) === -1) {
        if (this.number.comparedTo(this.min) === -1) {
          fixedNum = this.min;
        }
      }
      if (this.max != null && this.max.comparedTo(new BigNumber(0)) === 1) {
        if (this.number.comparedTo(this.max) === 1) {
          fixedNum = this.max;
        }
      }

      if (fixedNum != null) {
        if (this.autofix) {
          this._value = this.format(fixedNum, this).replace(MaskedNumber.UNMASKED_RADIX, this.radix);
          skip ||= oldValue === this._value && !flags.tail; // if not changed on tail it's still ok to proceed
        } else {
          accepted = false;
        }
      }
      accepted &&= Boolean(this._value.match(this._numberRegExp));
    }

    let appendDetails;
    if (!accepted) {
      this._value = oldValue;
      appendDetails = new ChangeDetails();
    } else {
      appendDetails = new ChangeDetails({
        inserted: this._value.slice(oldValue.length),
        rawInserted: skip ? '' : ch,
        skip,
      });
    }

    this._value = this._insertThousandsSeparators(this._value);
    const beforeTailValue = flags.tail && flags._beforeTailState ?
      flags._beforeTailState._value :
      this._value;
    const beforeTailSeparatorsCount = this._separatorsCountFromSlice(beforeTailValue);

    appendDetails.tailShift += (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length;
    return appendDetails;

    // if (!this.thousandsSeparator) return super._appendCharRaw(ch, flags);

    // const prevBeforeTailValue = flags.tail && flags._beforeTailState ?
    //   flags._beforeTailState._value :
    //   this._value;
    // const prevBeforeTailSeparatorsCount = this._separatorsCountFromSlice(prevBeforeTailValue);
    // this._value = this._removeThousandsSeparators(this.value);

    // const appendDetails = super._appendCharRaw(ch, flags);

    // this._value = this._insertThousandsSeparators(this._value);
    // const beforeTailValue = flags.tail && flags._beforeTailState ?
    //   flags._beforeTailState._value :
    //   this._value;
    // const beforeTailSeparatorsCount = this._separatorsCountFromSlice(beforeTailValue);

    // appendDetails.tailShift += (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length;
    // appendDetails.skip = !appendDetails.rawInserted && ch === this.thousandsSeparator;
    // return appendDetails;
  }

  get typedNullableValue() {
    if (this.nullable === true && this.value === '') {
      return null;
    }
    return this.typedValue;
  }

  set typedNullableValue(val) {
    if (!(this.nullable === true && this.value === '')) {
      this.typedValue = val;
    }
  }

  //   override _appendCharRaw (ch: string, flags: AppendFlags={}): ChangeDetails {
  //     const prevBeforeTailValue = flags.tail && flags._beforeTailState ?
  //       flags._beforeTailState._value :
  //       this._value;
  //     const prevBeforeTailSeparatorsCount = this._separatorsCountFromSlice(prevBeforeTailValue);
  //     this._value = this._removeThousandsSeparators(this.value);

  //     const oldValue = this._value;

  //     this._value += ch;

  //     const num = this.number;
  //     let accepted = BigNumber.isBigNumber(BigNumber(num));
  //     let skip = false;

  //     if (accepted) {
  //       let fixedNum;
  //       if (this.min != null && this.min < 0 && this.number < this.min) fixedNum = this.min;
  //       if (this.max != null && this.max > 0 && this.number > this.max) fixedNum = this.max;

  //       if (fixedNum != null) {
  //         if (this.autofix) {
  //           this._value = this.format(fixedNum, this).replace(MaskedNumber.UNMASKED_RADIX, this.radix);
  //           skip ||= oldValue === this._value && !flags.tail; // if not changed on tail it's still ok to proceed
  //         } else {
  //           accepted = false;
  //         }
  //       }
  //       accepted &&= Boolean(this._value.match(this._numberRegExp));
  //     }

  //     let appendDetails;
  //     if (!accepted) {
  //       this._value = oldValue;
  //       appendDetails = new ChangeDetails();
  //     } else {
  //       appendDetails = new ChangeDetails({
  //         inserted: this._value.slice(oldValue.length),
  //         rawInserted: skip ? '' : ch,
  //         skip,
  //       });
  //     }

  //     this._value = this._insertThousandsSeparators(this._value);
  //     const beforeTailValue = flags.tail && flags._beforeTailState ?
  //       flags._beforeTailState._value :
  //       this._value;
  //     const beforeTailSeparatorsCount = this._separatorsCountFromSlice(beforeTailValue);

  //     appendDetails.tailShift += (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length;
  //     return appendDetails;
  //   }

  _findSeparatorAround(pos: number): number {
    if (this.thousandsSeparator) {
      const searchFrom = pos - this.thousandsSeparator.length + 1;
      const separatorPos = this.value.indexOf(this.thousandsSeparator, searchFrom);
      if (separatorPos <= pos) return separatorPos;
    }

    return -1;
  }

  _adjustRangeWithSeparators(from: number, to: number): [number, number] {
    const separatorAroundFromPos = this._findSeparatorAround(from);
    if (separatorAroundFromPos >= 0) from = separatorAroundFromPos;

    const separatorAroundToPos = this._findSeparatorAround(to);
    if (separatorAroundToPos >= 0) to = separatorAroundToPos + this.thousandsSeparator.length;
    return [from, to];
  }


  override remove(fromPos: number = 0, toPos: number = this.displayValue.length): ChangeDetails {
    [fromPos, toPos] = this._adjustRangeWithSeparators(fromPos, toPos);

    const valueBeforePos = this.value.slice(0, fromPos);
    const valueAfterPos = this.value.slice(toPos);

    const prevBeforeTailSeparatorsCount = this._separatorsCount(valueBeforePos.length);
    this._value = this._insertThousandsSeparators(this._removeThousandsSeparators(valueBeforePos + valueAfterPos));
    const beforeTailSeparatorsCount = this._separatorsCountFromSlice(valueBeforePos);

    return new ChangeDetails({
      tailShift: (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length,
    });
  }

  override nearestInputPos(cursorPos: number, direction?: Direction): number {
    if (!this.thousandsSeparator) return cursorPos;

    switch (direction) {
      case DIRECTION.NONE:
      case DIRECTION.LEFT:
      case DIRECTION.FORCE_LEFT: {
        const separatorAtLeftPos = this._findSeparatorAround(cursorPos - 1);
        if (separatorAtLeftPos >= 0) {
          const separatorAtLeftEndPos = separatorAtLeftPos + this.thousandsSeparator.length;
          if (cursorPos < separatorAtLeftEndPos ||
            this.value.length <= separatorAtLeftEndPos ||
            direction === DIRECTION.FORCE_LEFT
          ) {
            return separatorAtLeftPos;
          }
        }
        break;
      }
      case DIRECTION.RIGHT:
      case DIRECTION.FORCE_RIGHT: {
        const separatorAtRightPos = this._findSeparatorAround(cursorPos);
        if (separatorAtRightPos >= 0) {
          return separatorAtRightPos + this.thousandsSeparator.length;
        }
      }
    }

    return cursorPos;
  }

  override doCommit() {
    if (this.value != null && this.value != '') {
      const number = this.number;
      let validnum = number;

      // check bounds
      if (this.min != null && validnum.toString().includes('-')) validnum = validnum.toString().length <= this.min.toString().length ? validnum : this.min;
      if (this.max != null && !validnum.toString().includes('-')) validnum = validnum.toString().length <= this.max.toString().length ? validnum : this.max;


      if (!new BigNumber(validnum).isEqualTo(number)) this.unmaskedValue = validnum.toString();

      let formatted = this.value;

      if (this.normalizeZeros) formatted = this._normalizeZeros(formatted);
      if (this.padFractionalZeros && this.scale > 0) formatted = this._padFractionalZeros(formatted);

      this._value = formatted;
    }

    super.doCommit();

    if (this.nullable === false && this.value === '') {
      this.value = '0';
    }
  }

  _normalizeZeros(value: string): string {
    const parts = this._removeThousandsSeparators(value).split(this.radix);

    // remove leading zeros
    parts[0] = parts[0].replace(/^(\D*)(0*)(\d*)/, (match, sign, zeros, num) => sign + num);
    // add leading zero
    if (value.length && !/\d$/.test(parts[0])) parts[0] = parts[0] + '0';

    if (parts.length > 1) {
      parts[1] = parts[1].replace(/0*$/, '');  // remove trailing zeros
      if (!parts[1].length) parts.length = 1;  // remove fractional
    }

    return this._insertThousandsSeparators(parts.join(this.radix));
  }

  _padFractionalZeros(value: string): string {
    if (!value) return value;

    const parts = value.split(this.radix);
    if (parts.length < 2) parts.push('');
    parts[1] = parts[1].padEnd(this.scale, '0');
    return parts.join(this.radix);
  }

  override doSkipInvalid(ch: string, flags: AppendFlags = {}, checkTail?: TailDetails): boolean {
    const dropFractional = this.scale === 0 && ch !== this.thousandsSeparator && (
      ch === this.radix ||
      ch === MaskedNumber.UNMASKED_RADIX ||
      this.mapToRadix.includes(ch)
    );
    return super.doSkipInvalid(ch, flags, checkTail) && !dropFractional;
  }

  // formato numerico con il punto
  override get unmaskedValue(): string {
    return this._removeThousandsSeparators(this._normalizeZeros(this.value))
      .replace(this.radix, MaskedNumber.UNMASKED_RADIX);
  }

  override set unmaskedValue(unmaskedValue: string) {
    super.unmaskedValue = unmaskedValue;
  }

  override get typedValue(): BigNumber {
    if (this.nullable && this.unmaskedValue == '') {
      return null;
    } else if (this.nullable && this.unmaskedValue == null) {
      return null;
    } else if (!this.nullable && this.unmaskedValue == null) {
      return new BigNumber(0);
    } else if (!this.nullable && this.unmaskedValue == '') {
      return new BigNumber(0);
    } else {
      return new BigNumber(this.unmaskedValue);
    }
  }

  override set typedValue(n: BigNumber) {
    this.rawInputValue = n.toString().replace(MaskedNumber.UNMASKED_RADIX, this.radix);
  }

  get number(): BigNumber {
    return this.typedValue;
  }

  set number(number: BigNumber) {
    this.typedValue = number;
  }

  get allowNegative(): boolean {
    return (this.min != null && this.min.toString().includes('-')) || (this.max != null && this.max.toString().includes('-'));
  }

  get allowPositive(): boolean {
    return (this.min != null && !this.min.toString().includes('-')) || (this.max != null && !this.max.toString().includes('-'));
  }

  override typedValueEquals(value: any): boolean {
    // handle  0 -> '' case (typed = 0 even if value = '')
    // for details see https://github.com/uNmAnNeR/imaskjs/issues/134

    if (BigNumber.isBigNumber(value)) {
      value = value.toFormat(BigNumber.getFormat(NumericHelper.getThousandSeparator(), NumericHelper.getDecimalSeparator(), this.thousandsSeparator?.length > 0));
    }

    return (
      super.typedValueEquals(value) ||
      MaskedNumber.EMPTY_VALUES.includes(value) && MaskedNumber.EMPTY_VALUES.includes(this.typedValue.toString())
    ) && !(value === 0 && this.value === '');
  }
}

export class NtsMaskedNumber extends MaskedNumber {

  nullable = false;
  faultBackValue = '0';
  faultBackValueFunc: null | (() => string) = null;
  // override _value = null;
  // element: ElementRef;

  constructor(opts: any) {
    super(opts);
    this.nullable = opts.nullable;
    this.faultBackValueFunc = opts.faultBackValueFunc;
  }


   override get typedValue (): number {
    return this.parse(this.unmaskedValue, this);
  }

  override set typedValue (n: number) {
    this.rawInputValue = this.format(n, this).replace(MaskedNumber.UNMASKED_RADIX, this.radix);
  }

  // override doPrepare (str: string, flags: any): any {
  //     if (this.nullable === false && str === '') {
  //         str = '0';
  //     }
  //     const doPrepareResult = super.doPrepare(str, flags) as [string, any];
  //     if (str === this.thousandsSeparator && this.mapToRadix.indexOf(this.thousandsSeparator) > -1 ) {
  //         if (this.element?.nativeElement?.value?.indexOf(this.radix) > -1) {
  //             doPrepareResult[0] = this.thousandsSeparator;
  //         }
  //     }
  //     return doPrepareResult;
  // }

  override doCommit() {

    const res = super.doCommit();

    if (this.nullable === false && this.value === '') {
      this.value = this.faultBackValueFunc ? this.faultBackValueFunc() : this.faultBackValue;
    }
    return res;
  }

  get typedNullableValue() {
    if (this.nullable === true && this.value === '') {
      return null;
    }
    return this.typedValue;
  }

  set typedNullableValue(val) {
    if (!(this.nullable === true && this.value === '')) {
      this.typedValue = val;
    }
  }
}

export class NumericHelper {

    static MAX_DECIMAL_DIGIT = 28;
    static MAX_INTEGER_DIGIT = 28;
    static MAX_DIGIT = 28;
    static MIN_DIGIT_FOR_BIGNUMBER = 15;
    static MAX_VALUE_INT32 = 2147483647;
    static MIN_VALUE_INT32 = -2147483647;
    static MAX_LENGTH_INT32 = 10;

    static thousandSeparator = this.getThousandSeparator();
    static decimalSeparator = this.getDecimalSeparator();

    static currentNumeralLocale = null;

    static NUMBER_MASK_BLOCK = 'Number';

    static initCurrentNumericLocale() {

      if (this.currentNumeralLocale != null) {
          return;
      }

      // load correct culture file
      const language = navigator.language;

      try {
          if (NumeralService.Current.locales[language.toLowerCase()] == null) {
              // Fallback su italiano
              NumeralService.Current.locale('it');
          } else {
              NumeralService.Current.locale(language.toLowerCase());
          }

      } catch (e) {
          // Fallback su italiano in caso di errrore
          NumeralService.Current.locale('it');
      }
    }

    static getThousandSeparator() {
      this.initCurrentNumericLocale();
      return (NumeralService.Current as any).localeData(NumeralService.Current.locale()).delimiters.thousands;
    }

    static getDecimalSeparator() {
      this.initCurrentNumericLocale();
      return (NumeralService.Current as any).localeData(NumeralService.Current.locale()).delimiters.decimal;
    }

    static getDecimalDigits(propertyName: string, integerLimit: number | null, decimalLimit: number | null): number {
        if (integerLimit != null && decimalLimit != null && (integerLimit + decimalLimit) > this.MAX_DIGIT) {
            throw new Error("I metadati della propery " + propertyName + " superano i limite massimo di " + this.MAX_DIGIT);
        }
        if (integerLimit == null && decimalLimit != null) {
            return decimalLimit < this.MAX_DECIMAL_DIGIT ? decimalLimit : this.MAX_DECIMAL_DIGIT;
        }
        if (integerLimit != null && decimalLimit == null) {
            return 0;
        }
        if (integerLimit == null && decimalLimit == null) {
            return 0;
        }
        if (integerLimit != null && decimalLimit != null) {
            return decimalLimit < this.MAX_DECIMAL_DIGIT ? decimalLimit : this.MAX_DECIMAL_DIGIT;
        }
        return 0;
    }

    static getIntegerDigits(propertyName: string, integerLimit: number | null, decimalLimit: number | null): number {
        if (integerLimit != null && decimalLimit != null && (integerLimit + decimalLimit) > this.MAX_DIGIT) {
            throw new Error("I metadati della propery " + propertyName + " superano i limite massimo di " + this.MAX_DIGIT);
        }
        if (integerLimit == null && decimalLimit != null) {
            return (this.MAX_DIGIT - decimalLimit) > this.MAX_INTEGER_DIGIT ? this.MAX_INTEGER_DIGIT : (this.MAX_DIGIT - decimalLimit);
        }
        if (integerLimit != null && decimalLimit == null) {
            return integerLimit < this.MAX_INTEGER_DIGIT ? integerLimit : this.MAX_INTEGER_DIGIT;
        }
        if (integerLimit == null && decimalLimit == null) {
            return this.MAX_INTEGER_DIGIT < this.MAX_DIGIT ? this.MAX_INTEGER_DIGIT : this.MAX_DIGIT;
        }
        if (integerLimit != null && decimalLimit != null) {
            return integerLimit < this.MAX_INTEGER_DIGIT ? integerLimit : this.MAX_INTEGER_DIGIT;
        }
        return 0;
    }

    static getMaxMaskSettings(maskSettings: NumericMaskSettings) {
        let max = null;
        let maxString = '';
        if (maskSettings.integerLimit > 0) {
        for (let i = 0; i < maskSettings.integerLimit; i++) {
            maxString += '9';
        }
        max = parseFloat(maxString);
        }

        if (maskSettings.max != null) {
        max = (max != null) ?
            (maskSettings.max > max ? max : maskSettings.max) :
            maskSettings.max;
        }
        return max;
    }

    static getMaskedNumberOptions(
        maskSettings: NumericMaskSettings,
        element?: ElementRef
    ): any {
        if (maskSettings.isBigNumber) {
          return {
            scale: maskSettings.decimalLimit == null ? null : maskSettings.decimalLimit,
            signed: true,
            normalizeZeros: maskSettings?.normalizeZeros ?? false,
            overwrite: 'shift',
            max: new BigNumber(maskSettings.max),
            min: new BigNumber(maskSettings.min),
            thousandsSeparator: maskSettings.useThousandSeparator ? this.thousandSeparator : '',
            radix: this.decimalSeparator,
            mapToRadix: maskSettings.useThousandSeparator ? [] : [this.thousandSeparator],
            nullable: maskSettings.nullable,
            lazy: maskSettings?.lazy ?? false,
            eager: maskSettings?.eager ?? undefined,
            padFractionalZeros: maskSettings?.padFractionalZeros ?? true,
            faultBackValueFunc: maskSettings?.faultBackValueFunc ? maskSettings?.faultBackValueFunc : null
          };
        } else {
          return {
            mask: Number,
            scale: maskSettings.decimalLimit == null ? null : maskSettings.decimalLimit,
            signed: true,
            normalizeZeros: maskSettings?.normalizeZeros ?? false,
            overwrite: 'shift',
            max: this.getMaxMaskSettings(maskSettings),
            min: maskSettings?.min,
            thousandsSeparator: maskSettings.useThousandSeparator ? this.thousandSeparator : '',
            radix: this.decimalSeparator,
            mapToRadix: maskSettings.useThousandSeparator ? [] : [this.thousandSeparator],
            nullable: maskSettings.nullable,
            lazy: maskSettings?.lazy ?? false,
            eager: maskSettings?.eager ?? undefined,
            element,
            padFractionalZeros: maskSettings?.padFractionalZeros ?? true,
            faultBackValueFunc: maskSettings?.faultBackValueFunc ? maskSettings?.faultBackValueFunc : null
          };
        }
    }

    static getMaskedValue(
        maskSettings: NumericMaskSettings,
        value: number | BigNumber
    ): number | BigNumber {
        if (maskSettings) {

          const maskConfiguration = NumericHelper.getMaskConfiguration(maskSettings);

          const masked = IMask.createMask({
            ...maskConfiguration
          });

          let valueToResolve = value?.toString() ?? '';
          if (maskSettings.isBigNumber && value?.toString()?.length > 0) {
              if (BigNumber.isBigNumber(value)) {
              valueToResolve = value.toFormat({ decimalSeparator: NumericHelper.getDecimalSeparator(), groupSeparator: '' });
              };
          }
          masked.resolve(valueToResolve);

          if (masked instanceof MaskedPattern) {
            const typedMask: NtsMaskedBigNumber | NtsMaskedNumber = this.getNumericTypedMask(maskSettings);
            const unmaskedValue = masked.unmaskedValue.split('.').join(this.decimalSeparator);
            typedMask.resolve(unmaskedValue);
            value = typedMask.typedNullableValue;
            (typedMask as any)?.destroy && (typedMask as any).destroy();
          } else {
            value = masked.typedNullableValue;
          }
          masked?.destroy && masked.destroy();

        }
        return value;
    }

    /**
     * Calcola il valore massimo partendo dai numeri interi e decimali massimi
     *
     * @param decimalDigits numero di decimali massimi
     * @param integerDigits numero di interi massimi
     * @returns il numero massimo in formato numerico se inferiore a 14 cifre (decimalDigits + integerDigits) altrimenti ritorna un BigNumber
     */
    static calculateMaxValue(
        decimalDigits: number,
        integerDigits: number,
        isDecimal: boolean
    ): number|BigNumber {
        const integerDigitsString = "9".repeat(integerDigits);
        let decimalDigitsString = "";
        if (decimalDigits > 0) {
            decimalDigitsString = "9".repeat(decimalDigits);
        }

        const stringWithSeparator = integerDigitsString + (decimalDigitsString?.length > 0 ? ('.' + decimalDigitsString) : '');

        if (isDecimal === false) {
            let maxValue = parseFloat(stringWithSeparator);
            if (maxValue > NumericHelper.MAX_VALUE_INT32) {
                return NumericHelper.MAX_VALUE_INT32
            }
        }

        if ((integerDigitsString?.length + decimalDigitsString?.length) >= NumericHelper.MIN_DIGIT_FOR_BIGNUMBER) {
            return new BigNumber(stringWithSeparator)
        }
        return parseFloat(stringWithSeparator);
    }

    /**
     * Calcola il valore minimo partendo dai numeri interi e decimali massimi
     *
     * @param decimalDigits numero di decimali massimi
     * @param integerDigits numero di interi massimi
     * @returns il numero minimo in formato numerico se inferiore a [BaseNumericPropertyViewModel.MIN_DIGIT_FOR_BIGNUMBER] cifre (decimalDigits + integerDigits) altrimenti ritorna un BigNumber
     */
    static calculateMinValue(
        decimalDigits: number,
        integerDigits: number,
        isDecimal: boolean
    ): number|BigNumber {
        const integerDigitsString = "9".repeat(integerDigits);
        let decimalDigitsString = "";
        if (decimalDigits > 0) {
            decimalDigitsString = "9".repeat(decimalDigits);
        }

        const stringWithSeparator = integerDigitsString + (decimalDigitsString?.length > 0 ? ('.' + decimalDigitsString) : '');

        if (isDecimal === false) {
            let minValue = parseFloat(stringWithSeparator) * -1;
            if (minValue < NumericHelper.MIN_VALUE_INT32) {
                return NumericHelper.MIN_VALUE_INT32
            }
        }

        if ((integerDigitsString?.length + decimalDigitsString?.length) >= NumericHelper.MIN_DIGIT_FOR_BIGNUMBER) {
            return new BigNumber(stringWithSeparator).multipliedBy(new BigNumber(-1))
        }
        return parseFloat(stringWithSeparator) * -1;
    }

    static getNumericTypedMask(maskSettings: NumericMaskSettings, numericBox?: ElementRef) {
      if (maskSettings.isBigNumber) {
        return new NtsMaskedBigNumber(
            this.getMaskedNumberOptions(maskSettings, numericBox)
        );
      } else {
        return new NtsMaskedNumber(
            this.getMaskedNumberOptions(maskSettings, numericBox)
        );
      }
    }

    /**
     * Ritorna l'oggetto mask corretto sia che sia number che bignumber
     *
     * @param maskSettings
     * @param numericBox
     * @returns
     */
    static getMaskConfiguration(maskSettings: NumericMaskSettings, numericBox?: ElementRef) {
        const typedMask: NtsMaskedBigNumber | NtsMaskedNumber = this.getNumericTypedMask(maskSettings, numericBox);
        let mask: any //: MaskedOptions<NtsMaskedBigNumber | NtsMaskedNumber>;

        if (maskSettings.patternMask) {
          let blocks = null;

          for (const element of Object.entries(maskSettings.patternMask.blocks)) {
            const [key, value] = element;
            if (!blocks) {
              blocks = {};
            }

            if (value.mask === this.NUMBER_MASK_BLOCK) {
              blocks[key] = {
                mask: typedMask,
                expose: true
              };
            } else {
              blocks[key] = { ...value };
            }
          }

          mask = {
            mask: maskSettings.patternMask.mask,
            lazy: maskSettings?.lazy ?? false,
            eager: maskSettings?.eager ?? undefined,
            blocks
          };

        } else {
          mask = {
            mask: typedMask,
            lazy: maskSettings?.lazy ?? false,
            eager: maskSettings?.eager ?? undefined
          };
        }

        return mask;
    }

    /**
     * @deprecated non più usato
     *
     * Ritorna la stringa di formato numerico per numeral.js
     *
     * @param integerLimit
     * @param decimalLimit
     * @param useThousandSeparator
     * @param propertyName
     * @returns
     */
    static getNumericFormat(
      integerLimit: number | null,
      decimalLimit: number | null,
      useThousandSeparator: boolean,
      propertyName: string = ''
    ): string {
      let format;
      if (decimalLimit === 0 || !decimalLimit) {
          // è un int
          format = useThousandSeparator ? '0,0' : '0';
      } else {
          // è un decimal
          format = useThousandSeparator ? '0,0.' : '0.';
          if (decimalLimit) {
              const digits = NumericHelper.getDecimalDigits(propertyName, integerLimit, decimalLimit);
              format += '0'.repeat(digits);
          }
      }
      return format;
    }

    static getFormattedValue(value: number | BigNumber, maskSettings: NumericMaskSettings): string {

      const maskConfiguration = NumericHelper.getMaskConfiguration(maskSettings);

      const masked = IMask.createMask({
        ...maskConfiguration
      });

      let valueToResolve = value?.toString() ?? '';
      if (maskSettings.isBigNumber && value?.toString()?.length > 0) {
          if (BigNumber.isBigNumber(value)) {
          valueToResolve = value.toFormat({ decimalSeparator: NumericHelper.getDecimalSeparator(), groupSeparator: '' });
          };
      }
      masked.resolve(valueToResolve);
      const displayValue = masked.displayValue;
      masked?.destroy && masked?.destroy();
      return displayValue;
    }

}
